import type { DocumentData, FirestoreDataConverter } from 'firebase/firestore'
import {
  collection,
  orderBy,
  query,
  serverTimestamp,
} from 'firebase/firestore'
import converter from './commentConverter'
import createService from '@/composables/db/useServiceFactory'
import type { DSComment, DSUser } from '@/types'

export default function useCommentBase(entityName: string, entityId: string) {
  const db = useFirestore()
  const collectionRef
    = collection(db, `${entityName}/${entityId}/comments`)
  const service = createService<DSComment>(`${entityName}/${entityId}/comments`)
  const modelConverter = converter()

  const loadComments = () => {
    const q = query(
      collectionRef,
      orderBy('created', 'desc'),
    ).withConverter(modelConverter as FirestoreDataConverter<DSComment, DocumentData>)

    return useCollection<DSComment[]>(q, {
      ssrKey: `${entityName}-comments-${entityId}`,
    })
  }

  const sortComment = (commentList: DSComment[]) => {
    const parentCommentList = commentList.filter(v => !v.parentId)
    parentCommentList.forEach((v) => {
      v.replyList = commentList.filter(vv => vv.parentId === v.id)
    })
    return parentCommentList
  }

  const submitComment = async (comment: string, user: DSUser, userImageUrl: string) => {
    if (!user)
      return
    const timestamp = serverTimestamp()

    service.create({
      authorId: user.id,
      author: user.name,
      authorAvatar: userImageUrl,
      comment,
      created: timestamp,
      updated: timestamp,
      enabled: true,
      likeCount: 0,
      replyCount: 0,
      parentId: null,
    })
  }

  const replyComment = async (comment: string, commentId: string, user: DSUser, userImageUrl: string) => {
    if (!user)
      return
    const timestamp = serverTimestamp()

    return service.create({
      authorId: user.id,
      author: user.name,
      authorAvatar: userImageUrl,
      comment,
      created: timestamp,
      updated: timestamp,
      enabled: true,
      likeCount: 0,
      replyCount: 0,
      parentId: commentId,
    })
  }

  const pressLike = (commentId: string) => {
    service.incrementField(commentId, 'likeCount', 1)
  }

  return {
    pressLike,
    loadComments,
    sortComment,
    submitComment,
    replyComment,
  }
}
